import { Component, Inject, type OnInit, type OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { IArticleTeaser } from '@candyland/generic/tt-com-shared-overviews';
import { TT_SHARED_API_ENDPOINT } from '@candyland/angular/shared/model-nmo';
import { AbTestingService } from '@candyland/angular/shared/ab-testing';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DriveTrackingService } from '@candyland/angular/shared/data-access-nmo';
import { catchError, type Observable, of, Subscription, switchMap } from 'rxjs';
import { CookieService } from '@candyland/angular/shared/util-nmo';

@Component({
  selector: 'nmo-recommended-articles',
  templateUrl: './recommended-articles.component.html',
  styleUrls: ['./recommended-articles.component.scss'],
})
export class RecommendedArticlesComponent implements OnInit, OnDestroy {
  public articleTeasers$: Observable<RecommendedArticlesList> = this.http.get<RecommendedArticlesList>(
    `${this.ttApiEndpoint}/data/recommended-articles`,
    {
      headers: {
        authorization: this.oauth2Service.authorizationHeader(),
      },
    }
  );

  public articleTeasersPublic$: Observable<RecommendedArticlesList> = this.http.get<RecommendedArticlesList>(
    `${this.ttApiEndpoint}/data/recommended-articles/public/${this.getCookieUID()}`
  );

  public combinedArticleTeasers$: Observable<RecommendedArticlesList> = of(
    this.oauth2Service.authorizationHeader()
  ).pipe(
    switchMap((isLoggedIn) => (isLoggedIn !== 'Bearer null' ? this.articleTeasers$ : this.articleTeasersPublic$)),
    catchError(() => this.articleTeasersPublic$)
  );

  private subscriptions = new Subscription();

  constructor(
    private oauth2Service: OAuthService,
    private http: HttpClient,
    @Inject(TT_SHARED_API_ENDPOINT) private readonly ttApiEndpoint: string,
    private abTestingService: AbTestingService,
    private driveTrackingService: DriveTrackingService,
    private cookieService: CookieService
  ) {}

  ngOnInit(): void {
    if (
      !this.isBetaUser() ||
      (this.getCookieUID() === '' && this.oauth2Service.authorizationHeader() === 'Bearer null')
    ) {
      this.combinedArticleTeasers$ = of({ correlationId: '', teasers: {}, uuids: [] });
      return;
    }

    this.subscriptions.add(
      this.combinedArticleTeasers$.subscribe((articleTeasers: RecommendedArticlesList) => {
        Object.entries(articleTeasers.teasers).forEach(
          ([_key, teaser]: [key: string, teaser: IArticleTeaser], index: number) => {
            this.driveTrackingService.trackRecommendationExposure(teaser, articleTeasers.correlationId, index);
          }
        );
      })
    );
  }

  // TODO: Remove after Go-Live
  isBetaUser(): boolean {
    const config = this.abTestingService.getUserConfiguration('beta_version');
    return config.userAssignment === 'B';
  }

  driveTrackArticleClick(article_id: string, correlation_id: string): void {
    this.driveTrackingService.trackRecommendationClick(article_id, correlation_id);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  private getCookieUID(): string {
    const cookieValue = this.cookieService.getByPattern(/_sp_id\.\w{4}/);
    return cookieValue?.split('.')[0] ?? '';
  }
}

interface RecommendedArticlesList {
  correlationId: string;
  teasers: { [key: string]: IArticleTeaser };
  uuids: string[];
}
